import { produce, immerable, enableMapSet, isDraft } from 'immer';
import { clone, set } from 'lodash';

import { isMatchId } from '@mssgme/helpers';
export { isMatch, isMatchId } from '@mssgme/helpers';

enableMapSet();

export function createFuncReducer(initialState, handle) {
    return (state = initialState, action) => produce(state, (draft) => handle(draft, action));
}

export function reduceReducers(...reducers) {
    return (state, action) => {
        return reducers.reduce((acc, reducer) => {
            return reducer(acc, action);
        }, state);
    };
}

export function createActionCreator(type, produceAction) {
    const creator = (...args) => {
        if (produceAction) return { type, ...produceAction(...args) };
        const [payload, ...rest] = args;
        return { type, payload, meta: rest };
    };
    creator.type = type;
    creator.toString = () => type;
    return creator;
}

export const createAction = createActionCreator;

export class ReducerBuilder {
    constructor(state, opts) {
        opts = {
            immutable: true,
            ...opts,
        };
        this._state = state;
        this._immutable = opts.immutable;
        this._cases = {};
    }

    addCase(types, handler) {
        if (Array.isArray(types)) {
            for (let type of types) {
                this._cases[String(type)] = handler;
            }
        } else {
            this._cases[String(types)] = handler;
        }

        return this;
    }

    hasCase(type) {
        // eslint-disable-next-line no-prototype-builtins
        return this._cases.hasOwnProperty(type);
    }

    runCase(type, ...args) {
        return this._cases[type](...args);
    }

    createReducer() {
        let reducer;

        if (this._immutable) {
            reducer = (state = this._state, action) => {
                if (isDraft(state) && this.hasCase(action.type)) {
                    return this.runCase(action.type, state, action);
                }

                return produce(state, (draft) => {
                    if (this.hasCase(action.type)) {
                        return this.runCase(action.type, draft, action);
                    }
                });
            };
        } else {
            reducer = (state = this._state, action) => {
                return this.hasCase(action.type) ? this.runCase(action.type, state, action) : state;
            };
        }

        reducer.builder = this;

        return reducer;
    }
}

export function createReducerBuilder(initialState, handler, opts) {
    const builder = new ReducerBuilder(initialState, opts);

    if (typeof handler === 'function') {
        handler(builder);
    } else if (typeof handler === 'object') {
        for (const prop of Object.keys(handler)) {
            builder.addCase(prop, handler[prop]);
        }
    } else {
        throw new Error('Invalid handler type');
    }

    return builder;
}

export function createReducer(initialState, handler, opts) {
    if (typeof handler === 'function' && handler.length === 2) {
        return createFuncReducer(initialState, handler);
    } else {
        const builder = createReducerBuilder(initialState, handler, opts);
        return builder.createReducer();
    }
}

export class Collection {
    [immerable] = true;

    constructor(arr = [], identifier = '_id') {
        this._identifier = identifier;
        if (arr instanceof Collection) {
            arr = arr.toArray();
        }
        this.ids = arr.map((item) => item[identifier]);
        this.byIds = arr.reduce((acc, item) => {
            acc[item[identifier]] = item;
            return acc;
        }, {});
    }

    get length() {
        return this.ids.length;
    }

    add(items) {
        if (!Array.isArray(items)) items = [items];

        for (let item of items) {
            this.byIds[item[this._identifier]] = item;
            if (!this.exists(item)) {
                this.ids.push(item[this._identifier]);
            }
        }

        return this;
    }

    update(path, data) {
        set(this.byIds, path, data);
        return this;
    }

    exists(item) {
        return this.ids.indexOf(item[this._identifier]) >= 0;
    }

    remove(item) {
        this.ids = this.ids.filter((id) => id !== item[this._identifier]);
        delete this.ids[item[this._identifier]];

        return this;
    }

    removeById(itemId) {
        this.ids = this.ids.filter((id) => id !== itemId);
        delete this.ids[itemId];

        return this;
    }

    find(itemId) {
        return this.byIds[itemId];
    }

    clear() {
        this.ids = [];
        this.byIds = {};
        return this;
    }

    isEmpty() {
        return this.length === 0;
    }

    toArray() {
        return this.ids.map((id) => this.byIds[id]);
    }

    forEach(callback) {
        this.toArray().forEach(callback);
    }

    filter(callback) {
        return new Collection(this.toArray().forEach(callback));
    }

    map(callback) {
        return this.toArray().map(callback);
    }

    clone() {
        return clone(this);
    }
}

export function updateIn(arr, cond, val) {
    const index = arr.findIndex(cond);
    if (index >= 0) arr[index] = val;

    return index >= 0;
}

export function deleteIn(arr, cond) {
    const index = arr.findIndex(cond);
    if (index >= 0) return arr.splice(index, 1)[0];
}

/**
 * @template T
 * @param {object} options Reducer options.
 * @param {(state: any, action: any) => T[]} [options.collection] How to get the collection from the state. Can be
 * omitted, then the `state` is assumed to be the target collection.
 * @param {string} [options.match] Action property, that holds the entity ID. Can be omitted if the collection
 * does not support `delete` action.
 * @param {(action: any) => T} [options.result] Use this to pick the entity from `action` if the target entity is not
 * directly returned in the `action.result`, but is nested instead.
 * @param {boolean} [options.prepend=false] `true` if entity should be prepended to the target collection.
 * @param {(state: any, action: any, created?: T, updated?: T, deleted?: boolean) => void} [options.onUpdate] Callback
 * with the reducer result.
 * @returns {(state: any, action: any) => void} Reducer function.
 */
export function updateResource({
    collection: collectionSelector,
    match: entityProp,
    result: resultSelector,
    prepend,
    onUpdate,
}) {
    return (state, action) => {
        const collection = collectionSelector?.(state, action) || state;
        const result = resultSelector?.(action.result) || action.result;
        const entityId = result?._id || action[entityProp];
        const predicate = entityId && isMatchId(entityId);

        const deleted = !result; // 'delete' actions do not return anything
        const updated = result && predicate && collection.find(predicate); // already have entity in the collection
        const created = !(deleted || updated);

        if (deleted) {
            deleteIn(collection, predicate);
        } else if (updated) {
            updateIn(collection, predicate, result);
        } else if (prepend) {
            collection.unshift(result);
        } else {
            collection.push(result);
        }

        onUpdate?.({
            state,
            action,
            created: created && result,
            updated: updated && result,
            deleted,
        });
    };
}

